import { useEffect, useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import * as authService from '../services/authService'
import { useAuth } from '../contexts/AuthContext'
import '../styles/AuthCallback.css'

const TOKEN_STORAGE_KEY = 'aps_access_token'
const REFRESH_TOKEN_KEY = 'aps_refresh_token'
const TOKEN_EXPIRY_KEY = 'aps_token_expiry'

const AuthCallbackPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { setAuthData } = useAuth()
  const processedRef = useRef(false)

  useEffect(() => {
    // Prevent multiple callback processing (React 18 strict mode runs effects twice in dev)
    if (processedRef.current) {
      return
    }
    processedRef.current = true

    const handleCallback = async () => {
      try {
        // Get OAuth parameters from URL
        const code = searchParams.get('code')
        const state = searchParams.get('state')
        const oauthError = searchParams.get('error')
        const errorDescription = searchParams.get('error_description')

        // Check for OAuth errors from provider
        if (oauthError) {
          throw new Error(`OAuth error: ${oauthError} - ${errorDescription || 'Unknown error'}`)
        }

        if (!code) {
          throw new Error('No authorization code received from OAuth provider')
        }

        if (!state) {
          throw new Error('No state parameter received from OAuth provider')
        }

        // Retrieve requestId from localStorage
        // This was stored when the user clicked login
        const requestId = localStorage.getItem('oauth_request_id')
        console.log('[AuthCallback] Retrieved requestId:', requestId)

        // Clear the requestId from storage (one-time use)
        if (requestId) {
          localStorage.removeItem('oauth_request_id')
        }

        // Call backend to handle token exchange
        // Backend will:
        // 1. Validate state parameter (CSRF protection)
        // 2. Exchange code for access token with PKCE
        // 3. Store tokens in backend session
        // 4. Return user info
        let callbackResult
        try {
          callbackResult = await authService.handleOAuthCallback(code, state, requestId)
        } catch (callbackError) {
          // Check if error is due to code expiry
          if (callbackError.code === 'CODE_EXPIRED') {
            setError('Your login session has expired. Please try logging in again.')
            setLoading(false)
            return
          }
          throw callbackError
        }

        if (!callbackResult.success) {
          throw new Error('OAuth callback processing failed')
        }

        console.log('[AuthCallback] Callback result:', callbackResult)

        // Store tokens on frontend for quick access
        if (callbackResult.tokens && callbackResult.tokens.accessToken) {
          console.log('[AuthCallback] Storing tokens in localStorage')
          localStorage.setItem(TOKEN_STORAGE_KEY, callbackResult.tokens.accessToken)
          localStorage.setItem(TOKEN_EXPIRY_KEY, Date.now() + callbackResult.tokens.expiresIn * 1000)
          if (callbackResult.tokens.refreshToken) {
            localStorage.setItem(REFRESH_TOKEN_KEY, callbackResult.tokens.refreshToken)
          }

          // Update AuthContext so ProtectedRoute knows user is authenticated
          console.log('[AuthCallback] Updating AuthContext with user data:', callbackResult.user)
          setAuthData(callbackResult.user, callbackResult.tokens.accessToken)
        }

        // Redirect to dashboard
        navigate('/dashboard')
      } catch (err) {
        setError(err.message || 'Failed to complete authentication')
        setLoading(false)
      }
    }

    handleCallback()
  }, [searchParams, navigate, setAuthData])

  if (loading) {
    return (
      <div className="callback-container">
        <div className="callback-content">
          <div className="spinner"></div>
          <h2>Completing Authentication...</h2>
          <p>Please wait while we process your login</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="callback-container">
        <div className="callback-content error">
          <h2>Authentication Failed</h2>
          <p className="error-message">{error}</p>
          <button onClick={() => window.location.href = '/'} className="retry-button">
            Return to Login
          </button>
        </div>
      </div>
    )
  }

  return null
}

export default AuthCallbackPage
